import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Container, Skeleton, Stack, Typography } from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { PostCard } from './components/PostCard';
import { CategoryChips } from './components/CategoryChips';
import { useOpportunitiesFeed } from './useOpportunities';
import {
  POST_CATEGORIES,
  type PostCategory,
} from '../../shared/services/opportunities.service';

/**
 * Public feed page — vertical stack of post cards with a category-filter
 * chip strip. Logged-out visitors can read everything (SEO + viral sharing);
 * the "create post" CTA bounces them to /login in phase 2.
 */
export function OpportunitiesFeedPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Sync the active category with `?category=` so deep links + the back
  // button preserve the user's filter.
  const initialCategory = readCategoryParam(searchParams.get('category'));
  const [activeCategory, setActiveCategoryState] = useState<PostCategory | null>(initialCategory);

  const setActiveCategory = (next: PostCategory | null) => {
    setActiveCategoryState(next);
    const sp = new URLSearchParams(searchParams);
    if (next) sp.set('category', next);
    else sp.delete('category');
    setSearchParams(sp, { replace: true });
  };

  const { posts, isLoading, isError, hasMore, isFetchingMore, loadMore } =
    useOpportunitiesFeed(activeCategory);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, px: { xs: 0, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ px: { xs: 2, sm: 0 }, mb: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1.5, sm: 2 }}
          sx={{ mb: 0.5 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <LightbulbOutlinedIcon sx={{ color: 'primary.main' }} />
            <Typography
              variant="h3"
              sx={{ fontWeight: 800, letterSpacing: -0.5, fontSize: { xs: 26, md: 34 } }}
            >
              {t('opportunities.title')}
            </Typography>
          </Stack>
          {/* "Poster" CTA — public; the create page itself bounces anonymous
              visitors to /login with a return path. */}
          <Button
            component={RouterLink}
            to="/opportunities/new"
            variant="contained"
            startIcon={<AddRoundedIcon />}
            sx={{ borderRadius: 999, alignSelf: { xs: 'stretch', sm: 'auto' } }}
          >
            {t('opportunities.actions.create')}
          </Button>
        </Stack>
        <Typography color="text.secondary" sx={{ fontSize: { xs: 13, md: 15 } }}>
          {t('opportunities.subtitle')}
        </Typography>
      </Box>

      {/* Category strip */}
      <Box sx={{ px: { xs: 2, sm: 0 }, mb: 2 }}>
        <CategoryChips active={activeCategory} onChange={setActiveCategory} />
      </Box>

      {/* Feed — responsive grid: 1 col mobile, 2 cols tablet, 3 cols desktop */}
      <Box sx={{ px: { xs: 2, sm: 0 } }}>
        {isError ? (
          <Alert severity="error">{t('opportunities.loadError')}</Alert>
        ) : isLoading ? (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
            }}
          >
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rounded" height={220} />
            ))}
          </Box>
        ) : posts.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                // `minmax(0, 1fr)` is critical: plain `1fr` is shorthand for
                // `minmax(auto, 1fr)`, which means a column will expand past
                // its share when a child has an unbreakable run wider than
                // the column (long URLs, "aaaaaaa…" strings, etc.). That
                // would make one card visibly larger than its neighbours.
                // The 0 floor lets the column shrink to the track width and
                // the card's own overflow:hidden takes care of clipping.
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                // Stretch makes every card in a row match the tallest one
                // — combined with the fixed-size title/body slots inside
                // PostCard, every card across the whole feed ends up the
                // same height.
                alignItems: 'stretch',
                // Force every row to take the same vertical track size: the
                // tallest card on the whole page sets the height, and all
                // cards (across all rows) stretch to match it.
                gridAutoRows: '1fr',
              }}
            >
              {posts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
            </Box>
            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
                <Button
                  onClick={() => loadMore()}
                  disabled={isFetchingMore}
                  variant="outlined"
                  sx={{ borderRadius: 999, px: 3 }}
                >
                  {isFetchingMore ? t('opportunities.loading') : t('opportunities.loadMore')}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Container>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: { xs: 6, md: 10 },
        px: 2,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 4,
      }}
    >
      <LightbulbOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
      <Typography color="text.secondary">{t('opportunities.feedEmpty')}</Typography>
    </Box>
  );
}

function readCategoryParam(raw: string | null): PostCategory | null {
  if (!raw) return null;
  const upper = raw.toUpperCase() as PostCategory;
  return POST_CATEGORIES.includes(upper) ? upper : null;
}
